/**
 * TrialSignupModal — self-contained trial-signup popup (iter66l).
 *
 * Replaces the `/trial-signup` page navigation when triggered from
 * `/join-club` — the funnel skips the extra route and the user stays
 * on-page. Every piece of copy in the modal is derived DYNAMICALLY from
 * `/api/config/trial` so the exact same component correctly represents:
 *   • free trials  (magic-link, no card)
 *   • paid trials  (PayPlus checkout, recurring subscription)
 * without a single hard-coded string that could go out of sync with reality.
 *
 * Design language matches `CourseCheckoutModal.jsx`: rounded-[2rem] shell,
 * borderless surfaces separated only by bg tone, subtle shadows for depth,
 * dedicated pricing badge card, and a full-width CTA button.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  X, ArrowLeft, CreditCard, Mail, Lock, Sparkles, ShieldCheck, Clock, Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TrialSignupModal = ({ open, onClose, prefill = {}, source = 'join_club' }) => {
  const [cfg, setCfg] = useState(null);
  const [cfgLoading, setCfgLoading] = useState(true);
  const [form, setForm] = useState({
    email: prefill.email || '',
    name: prefill.name || '',
    phone: prefill.phone || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);          // free-trial success
  const [alreadyUsed, setAlreadyUsed] = useState(false);

  // Body-scroll lock while open
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [open]);

  // Fetch trial config once when modal opens (never re-fetch — the modal
  // is intentionally short-lived, and a mid-session cfg change would
  // create UX inconsistency).
  useEffect(() => {
    if (!open || cfg) return;
    setCfgLoading(true);
    fetch(`${API_URL}/api/config/trial`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setCfg(d))
      .catch(() => {})
      .finally(() => setCfgLoading(false));
  }, [open, cfg]);

  // Also fetch the current recurring price so the paid-mode renewal
  // disclaimer surfaces the exact renewal amount (single source of truth).
  const [renewalPrice, setRenewalPrice] = useState(299);
  useEffect(() => {
    if (!open) return;
    fetch(`${API_URL}/api/config/promotions/club_membership`)
      .then((r) => r.json())
      .then((d) => { if (d && d.regular_price) setRenewalPrice(d.regular_price); })
      .catch(() => {});
  }, [open]);

  if (!open) return null;

  const isPaid = cfg?.mode === 'paid';
  const priceLabel = cfg ? `₪${cfg.price_incl_vat ?? cfg.price}` : '';
  const durationLabel = cfg
    ? (cfg.duration_hours >= 24
        ? `${Math.round(cfg.duration_hours / 24)} ${cfg.duration_hours === 24 ? 'יום' : 'ימים'}`
        : `${cfg.duration_hours} שעות`)
    : '';

  // ── ALL COPY 100% DERIVED FROM cfg ──
  //   Every string below is either interpolated from cfg or gated on
  //   `isPaid`. No filler, no assumptions. The three fixed states
  //   (kicker/title/subtitle) also drop the field-name reference so
  //   they no longer contradict the actual form fields.
  const kicker = isPaid ? 'התחלת טריאל בתשלום' : 'התחלת טריאל חינם';
  const title = isPaid
    ? `${durationLabel} של גישה מלאה ב־${priceLabel}`
    : `${durationLabel} של גישה מלאה — חינם`;
  const subtitle = isPaid
    ? `מלאו פרטים ותועברו לתשלום מאובטח של ${priceLabel} כולל מע"מ. הגישה נפתחת מיד עם אישור החיוב.`
    : `מלאו פרטים ונשלח לכם קישור כניסה למייל. הגישה נפתחת מיד — לא נדרש כרטיס אשראי.`;

  // Renewal / expiry disclaimer below the CTA — exact truth per mode.
  const disclaimer = isPaid
    ? `${priceLabel} עכשיו · ${durationLabel} של גישה מלאה · בסיום המנוי מתחדש אוטומטית ב־₪${renewalPrice}/חודש + מע"מ · ביטול מיידי בכל עת.`
    : `${durationLabel} של גישה · לא נדרש כרטיס אשראי · הגישה מתפוגגת אוטומטית בסיום — אין חיוב.`;

  // Trust bullets in the info column — mirror the disclaimer visually.
  const trustBullets = isPaid
    ? [
        { icon: Clock,       text: `${durationLabel} של גישה מלאה מרגע החיוב` },
        { icon: ShieldCheck, text: 'תשלום מאובטח דרך PayPlus (PCI-DSS)' },
        { icon: Sparkles,    text: `בסיום התקופה — ₪${renewalPrice}/חודש. ביטול בקליק` },
      ]
    : [
        { icon: Clock,    text: `${durationLabel} של גישה מלאה מיד עם ההרשמה` },
        { icon: Mail,     text: 'קישור כניסה מיידי למייל (תקף 10 דקות)' },
        { icon: Lock,     text: 'לא נדרש כרטיס אשראי — אין חיוב אוטומטי' },
      ];

  const ctaLabel = isPaid
    ? <>המשך לתשלום {priceLabel} <ArrowLeft size={16} /></>
    : <>שלחו לי את קישור הכניסה <ArrowLeft size={16} /></>;
  const submittingLabel = isPaid ? 'מעביר לתשלום מאובטח...' : 'שולח קישור...';

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email.includes('@')) { setError('כתובת אימייל לא תקינה'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/magic-link/request`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, trial: true, source }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(d.detail || 'שגיאה בשליחת הקישור. נסו שוב.');
      } else if (d.trial_already_used) {
        setAlreadyUsed(true);
      } else if (d.requires_payment && d.checkout_url) {
        // Paid trial — PayPlus opens directly. The `?paid=1` return brings
        // the user to /trial-signup with the "check your email" state.
        window.location.href = d.checkout_url;
      } else {
        setSent(true);
      }
    } catch { setError('שגיאת רשת. נסו שוב.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div
      className="fixed inset-0 bg-surface-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="trial-signup-modal"
    >
      <div
        className="bg-surface-50 rounded-[1.5rem] md:rounded-[2rem] w-full max-w-lg md:max-w-3xl grid md:grid-cols-2 overflow-hidden shadow-2xl max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
      >
        {/* ── INFO PANE (right in RTL) ── */}
        <div className="p-6 md:p-8 relative overflow-y-auto">
          <button
            onClick={onClose}
            className="absolute top-3 left-3 text-surface-500 hover:text-surface-900 p-2 rounded-full hover:bg-white transition-colors"
            data-testid="trial-signup-modal-close"
            aria-label="סגירה"
          >
            <X size={18} />
          </button>

          {cfgLoading && !cfg ? (
            <div className="flex items-center justify-center h-full min-h-[280px]">
              <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
            </div>
          ) : (
            <>
              <p className="text-xs font-bold text-brand-600 mb-1 tracking-wide" data-testid="trial-modal-kicker">{kicker}</p>
              <h3 className="text-2xl md:text-3xl font-black text-surface-900 leading-tight mb-3" data-testid="trial-modal-title">
                {title}
              </h3>
              <p className="text-surface-600 text-sm mb-5 leading-relaxed" data-testid="trial-modal-subtitle">
                {subtitle}
              </p>

              {/* Price badge — only paid mode, exact same shape as CourseCheckoutModal */}
              {isPaid && (
                <div className="bg-white rounded-2xl p-4 shadow-sm shadow-surface-900/5 mb-5" data-testid="trial-modal-price-badge">
                  <p className="text-xs text-surface-500 mb-1">תשלום חד־פעמי לפתיחת הטריאל</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl md:text-4xl font-black text-brand-600" data-testid="trial-modal-price">
                      {priceLabel}
                    </span>
                    <span className="text-sm text-surface-500">כולל מע"מ</span>
                  </div>
                  <p className="text-[11px] text-surface-500 mt-1">
                    בסיום {durationLabel} המנוי מתחדש אוטומטית ב־₪{renewalPrice}/חודש
                  </p>
                </div>
              )}

              <ul className="space-y-2.5 text-xs md:text-sm text-surface-600" data-testid="trial-modal-perks">
                {trustBullets.map(({ icon: Icon, text }, i) => (
                  <li key={i} className="flex items-start gap-2 leading-relaxed">
                    <Icon size={15} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* ── FORM PANE (left in RTL) ── */}
        <div className="bg-white p-6 md:p-8 flex flex-col justify-center border-t md:border-t-0 md:border-r border-surface-100">
          {sent ? (
            <div className="text-center py-4" data-testid="trial-signup-sent">
              <div className="w-16 h-16 bg-brand-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-brand-600/30">
                <Mail size={26} className="text-white" />
              </div>
              <h4 className="text-xl font-bold text-surface-900 mb-2">הקישור בדרך אליכם</h4>
              <p className="text-surface-500 text-sm mb-3">
                שלחנו קישור כניסה{form.email ? <> ל־<strong className="text-surface-900">{form.email}</strong></> : null}.
              </p>
              <p className="text-xs text-surface-500 bg-surface-50 rounded-2xl p-3">
                💡 לא רואים את המייל? בדקו בתיקיית הספאם. הקישור תקף ל־10 דקות.
              </p>
              <button
                onClick={onClose}
                className="mt-4 text-sm text-brand-600 hover:text-brand-700 font-bold"
                data-testid="trial-signup-sent-close"
              >
                חזרה לדף
              </button>
            </div>
          ) : alreadyUsed ? (
            <div className="text-center py-4" data-testid="trial-already-used">
              <div className="w-16 h-16 bg-brand-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Sparkles size={26} className="text-brand-600" />
              </div>
              <h4 className="text-xl font-bold text-surface-900 mb-2">כבר ניצלתם את הטריאל</h4>
              <p className="text-surface-500 text-sm mb-4 leading-relaxed">
                טריאל ניתן פעם אחת. הצטרפו למועדון במחיר המלא כדי להמשיך.
              </p>
              <Link
                to="/join-club#checkout"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-700 text-white px-6 py-3 rounded-2xl font-bold text-sm shadow-lg shadow-brand-600/30 transition-colors"
                data-testid="trial-already-used-cta"
              >
                <CreditCard size={16} /> הצטרפו למועדון
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="trial-signup-form">
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="שם מלא"
                className="w-full bg-surface-50 rounded-2xl px-4 py-3.5 text-sm focus:outline-none focus:shadow-md transition-shadow text-surface-900 placeholder:text-surface-500"
                data-testid="trial-signup-name"
              />
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="אימייל *"
                className="w-full bg-surface-50 rounded-2xl px-4 py-3.5 text-sm focus:outline-none focus:shadow-md transition-shadow text-surface-900 placeholder:text-surface-500"
                data-testid="trial-signup-email"
              />
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="טלפון (אופציונלי)"
                className="w-full bg-surface-50 rounded-2xl px-4 py-3.5 text-sm focus:outline-none focus:shadow-md transition-shadow text-surface-900 placeholder:text-surface-500"
                data-testid="trial-signup-phone"
              />
              {error && (
                <div className="bg-rose-50 text-rose-700 text-sm px-4 py-3 rounded-2xl" data-testid="trial-signup-error">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting || cfgLoading}
                className="w-full bg-brand-600 hover:bg-brand-700 text-white py-3.5 rounded-2xl font-bold text-sm disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg shadow-brand-600/30 transition-colors"
                data-testid="trial-signup-submit"
              >
                {submitting ? submittingLabel : ctaLabel}
              </button>
              <p className="text-xs text-surface-500 text-center pt-1 leading-relaxed" data-testid="trial-signup-disclaimer">
                {disclaimer}
              </p>
              <p className="text-[11px] text-surface-500 text-center leading-relaxed">
                בהמשך אתם מסכימים ל־<Link to="/terms" className="underline">תנאי השימוש</Link> ול־<Link to="/privacy" className="underline">מדיניות הפרטיות</Link>.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrialSignupModal;
